import { CourseStudentDocument } from "api/courseStudentsApi";
import { useCourseDocumentsOptions } from "api/hooks";
import { CourseDocumentForm } from "pages/Course/Documents/CourseDocumentForm";
import React, { useRef } from "react";
import { useToaster } from "utils";
import { PaziaButton, PaziaLoader } from "components/atoms";
import type { GenerationState } from "./GenerateDocumentsV2";

interface GenerateCourseDocumentsProps {
  onBack: (refresh?: boolean) => void;
  courseId: number;
  students: CourseStudentDocument[];
}

export const GenerateCourseDocuments = ({ onBack, courseId, students }: GenerateCourseDocumentsProps) => {
  const { showError } = useToaster();
  const [submitting, setSubmitting] = React.useState(false);
  // Dokumenty 2.0 are generated in this browser: the screen must not be left while that runs, and the list is stale
  // once any document was saved, even when the batch did not finish.
  const [generation, setGeneration] = React.useState<GenerationState>({ running: false, generated: false });
  const { options: courseDocumentOptions, loading: loadingOptions } = useCourseDocumentsOptions(Number(courseId));
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const onGenerateClick = async () => {
    // Nothing to run yet: no document chosen, or the 2.0 screen is still being loaded. Spinning would never end.
    if (!submitButtonRef.current) return;
    setSubmitting(true);
    try {
      submitButtonRef.current.click();
    } catch {
      showError("Wystąpił błąd podczas generowania dokumentów");
    }
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <div className="py-4 sm:p-6">
        {/* Header with back button */}
        <div className="mb-8">
          <button
            onClick={() => onBack(generation.generated)}
            disabled={generation.running}
            className="group mb-6 flex items-center gap-3 rounded-lg bg-white px-4 py-2 text-slate-600 shadow-sm transition-all duration-200 hover:bg-slate-50 hover:text-slate-800 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100"
            aria-label="Powrót do listy dokumentów"
          >
            <span className="mr-2 flex h-5 w-5 items-center justify-center">
              {/* Simple left arrow SVG */}
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </span>
            <span className="font-medium">Powrót do listy dokumentów</span>
          </button>

          {/* Title and Generate button */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Generowanie dokumentów</h1>
              <p className="mt-2 text-slate-600 dark:text-slate-400">
                Wybierz dokumenty do wygenerowania dla wybranych studentów
              </p>
            </div>
            <PaziaButton
              type="success"
              title="Generuj dokumenty"
              disabled={submitting || loadingOptions}
              processing={submitting}
              onClick={onGenerateClick}
              className="shrink-0"
            />
          </div>
        </div>

        {/* Selected students info */}
        <div className="mb-8 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
          <div className="border-b border-slate-200 bg-slate-50 px-3 py-4 sm:px-6 dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400">
                <span className="font-semibold">{students.length}</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Wybrani studenci</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {students.length === 1 ? "1 student wybrany" : `${students.length} studentów wybranych`}
                </p>
              </div>
            </div>
          </div>
          <div className="p-3 sm:p-6">
            <div className="max-h-48 overflow-y-auto rounded-lg bg-slate-50 p-3 dark:bg-slate-900">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {students.map((student) => (
                  <div
                    key={student.id}
                    className="flex items-center gap-2 rounded-md bg-white p-2 shadow-sm transition-all duration-200 hover:shadow-md dark:bg-slate-800 dark:hover:bg-slate-700"
                  >
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                      {student.firstName.charAt(0)}
                      {student.surname.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-slate-900 dark:text-slate-100">
                        {student.firstName} {student.surname}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Form content */}
        <div className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
          <div className="border-b border-slate-200 bg-slate-50 px-3 py-4 sm:px-6 dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Konfiguracja dokumentów</h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Wybierz typy dokumentów do wygenerowania</p>
          </div>
          <div className="p-3 sm:p-6">
            {loadingOptions ? (
              <div className="flex flex-col items-center justify-center py-16">
                <PaziaLoader position="center" />
                <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">Ładowanie opcji dokumentów...</p>
              </div>
            ) : (
              <div className="course-document-form">
                <CourseDocumentForm
                  submitButtonRef={submitButtonRef}
                  studentsIds={students.map((x) => x.id)}
                  courseDocumentOptions={courseDocumentOptions}
                  onSubmit={() => {
                    onBack(true);
                    setSubmitting(false);
                  }}
                  onGenerationState={(state) => {
                    // `generated` only ever turns on: a document saved earlier keeps the list out of date.
                    setGeneration((previous) => ({
                      running: state.running,
                      generated: previous.generated || state.generated,
                    }));
                    // A batch that ended with failures (or never started) must not leave the button spinning.
                    if (!state.running) setSubmitting(false);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
